import type { createCanvas, ImageData } from 'canvas';
import { keys } from 'ramda';

import { createAlineCanvas } from '../canvas';

interface Result {
  alphabetic: number;
  ascent: number;
  baseline: number;
  bottom: number;
  capHeight: number;
  descent: number;
  em: number;
  hanging: number;
  ideographic: number;
  lineHeight: number;
  lower: number;
  median: number;
  middle: number;
  overshoot: number;
  tittle: number;
  top: number;
  upper: number;
  xHeight: number;
}
interface Measure {
  cache: Record<string, Result>;
  canvas: ReturnType<typeof createCanvas> | null;
}
const measure: Measure = { cache: {}, canvas: null };

/**
 * Lazy initialization of canvas to avoid font registration during module import.
 */
function getOrCreateCanvas(): ReturnType<typeof createCanvas> {
  if (!measure.canvas) measure.canvas = createAlineCanvas();

  return measure.canvas;
}

interface Options {
  ascent?: string;
  canvas?: ReturnType<typeof createCanvas>;
  descent?: string;
  em?: number;
  family?: string[] | string;
  fontSize?: number;
  fontStyle?: string;
  fontWeight?: string;
  lower?: string;
  origin?: 'top' | 'baseline';
  overshoot?: string;
  size?: number;
  style?: string;
  tittle?: string;
  upper?: string;
  weight?: string;
}

/**
 * Carbon copy implementation of font-measure (https://www.npmjs.com/package/font-measure), but using Node canvas.
 */
export function measureFont(font: string[] | string, o: Options = {}): Result {
  if (typeof font === 'string' || Array.isArray(font)) o.family = font;

  const family = Array.isArray(o.family) ? o.family.join(', ') : o.family;
  if (!family) throw Error('`family` must be defined');

  const fs = o.size || o.fontSize || o.em || 48;
  const weight = o.weight || o.fontWeight || '';
  const style = o.style || o.fontStyle || '';
  const fontString = [style, weight, fs].join(' ') + 'px ' + family;
  const origin = o.origin || 'top';

  if (measure.cache[family]) {
    // return more precise values if cache has them
    if (fs <= measure.cache[family].em) return applyOrigin(measure.cache[family], origin);
  }

  const canvas = o.canvas || getOrCreateCanvas();
  const ctx = canvas.getContext('2d');
  const chars = {
    ascent: o.ascent !== undefined ? o.ascent : 'h',
    descent: o.descent !== undefined ? o.descent : 'p',
    lower: o.lower !== undefined ? o.lower : 'x',
    overshoot: o.overshoot !== undefined ? o.overshoot : 'O',
    tittle: o.tittle !== undefined ? o.tittle : 'i',
    upper: o.upper !== undefined ? o.upper : 'H',
  };
  const l = Math.ceil(fs * 1.5);
  canvas.height = l;
  canvas.width = l * 0.5;
  ctx.font = fontString;

  const char = 'H';
  const result: Result = {
    alphabetic: 0,
    ascent: 0,
    baseline: 0,
    bottom: 0,
    capHeight: 0,
    descent: 0,
    em: 0,
    hanging: 0,
    ideographic: 0,
    lineHeight: 0,
    lower: 0,
    median: 0,
    middle: 0,
    overshoot: 0,
    tittle: 0,
    top: 0,
    upper: 0,
    xHeight: 0,
  };

  // measure line-height
  ctx.clearRect(0, 0, l, l);
  ctx.textBaseline = 'top';
  ctx.fillStyle = 'black';
  ctx.fillText(char, 0, 0);
  const topPx = firstTop(ctx.getImageData(0, 0, l, l));
  ctx.clearRect(0, 0, l, l);
  ctx.textBaseline = 'bottom';
  ctx.fillText(char, 0, l);
  const bottomPx = firstTop(ctx.getImageData(0, 0, l, l));
  result.lineHeight = result.bottom = l - bottomPx + topPx;

  // measure baseline
  ctx.clearRect(0, 0, l, l);
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(char, 0, l);
  const baselinePx = firstTop(ctx.getImageData(0, 0, l, l));
  const baseline = l - baselinePx - 1 + topPx;
  result.baseline = result.alphabetic = baseline;

  // measure median
  ctx.clearRect(0, 0, l, l);
  ctx.textBaseline = 'middle';
  ctx.fillText(char, 0, l * 0.5);
  const medianPx = firstTop(ctx.getImageData(0, 0, l, l));
  result.median = result.middle = l - medianPx - 1 + topPx - l * 0.5;

  // measure hanging
  ctx.clearRect(0, 0, l, l);
  ctx.textBaseline = 'hanging';
  ctx.fillText(char, 0, l * 0.5);
  const hangingPx = firstTop(ctx.getImageData(0, 0, l, l));
  result.hanging = l - hangingPx - 1 + topPx - l * 0.5;

  // measure ideographic
  ctx.clearRect(0, 0, l, l);
  ctx.textBaseline = 'ideographic';
  ctx.fillText(char, 0, l);
  const ideographicPx = firstTop(ctx.getImageData(0, 0, l, l));
  result.ideographic = l - ideographicPx - 1 + topPx;

  // measure cap
  if (chars.upper) {
    ctx.clearRect(0, 0, l, l);
    ctx.textBaseline = 'top';
    ctx.fillText(chars.upper, 0, 0);
    result.upper = firstTop(ctx.getImageData(0, 0, l, l));
    result.capHeight = result.baseline - result.upper;
  }

  // measure x
  if (chars.lower) {
    ctx.clearRect(0, 0, l, l);
    ctx.textBaseline = 'top';
    ctx.fillText(chars.lower, 0, 0);
    result.lower = firstTop(ctx.getImageData(0, 0, l, l));
    result.xHeight = result.baseline - result.lower;
  }

  // measure tittle
  if (chars.tittle) {
    ctx.clearRect(0, 0, l, l);
    ctx.textBaseline = 'top';
    ctx.fillText(chars.tittle, 0, 0);
    result.tittle = firstTop(ctx.getImageData(0, 0, l, l));
  }

  // measure ascent
  if (chars.ascent) {
    ctx.clearRect(0, 0, l, l);
    ctx.textBaseline = 'top';
    ctx.fillText(chars.ascent, 0, 0);
    result.ascent = firstTop(ctx.getImageData(0, 0, l, l));
  }

  // measure descent
  if (chars.descent) {
    ctx.clearRect(0, 0, l, l);
    ctx.textBaseline = 'top';
    ctx.fillText(chars.descent, 0, 0);
    result.descent = firstBottom(ctx.getImageData(0, 0, l, l));
  }

  // measure overshoot
  if (chars.overshoot) {
    ctx.clearRect(0, 0, l, l);
    ctx.textBaseline = 'top';
    ctx.fillText(chars.overshoot, 0, 0);
    const overshootPx = firstBottom(ctx.getImageData(0, 0, l, l));
    result.overshoot = overshootPx - baseline;
  }

  // normalize result
  for (const name of keys(result)) result[name] /= fs;

  result.em = fs;
  measure.cache[family] = result;

  return applyOrigin(result, origin);
}

function applyOrigin(obj: Result, origin: number | keyof Result): Result {
  const res = { ...obj };
  if (typeof origin === 'string') origin = obj[origin];
  for (const name of keys(obj)) {
    if (name === 'em') continue;
    res[name] = obj[name] - origin;
  }
  return res;
}

function firstTop(iData: ImageData): number {
  const l = iData.height;
  const data = iData.data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] !== 0) {
      return Math.floor(((i - 3) * 0.25) / l);
    }
  }
  return 0;
}

function firstBottom(iData: ImageData): number {
  const l = iData.height;
  const data = iData.data;
  for (let i = data.length - 1; i > 0; i -= 4) {
    if (data[i] !== 0) {
      return Math.floor(((i - 3) * 0.25) / l);
    }
  }
  return 0;
}
